import { type CSSProperties, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Markdown from 'react-markdown';
import { formatDateOnlyTime } from '@witmetrics/utils/time';
import { formatUserName } from '@witmetrics/utils/strings';
import { getProjectTitleWithPatient } from '@witmetrics/unison-core';
import type {
  DecoratedOrderTask,
  DecoratedUnisonProjectTask,
} from '@witmetrics/utils';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import EventMenu from '@/menus/EventMenu';
import UnisonProjectTaskDialog from '@/dialogs/UnisonProjectTaskDialog';
import OrderTaskDialog from '@/dialogs/OrderTaskDialog';
import ProfilePicture from '@/components/ProfilePicture';
import Tooltip from '@/components/Tooltip';
import TruncatedText from '@/components/TruncatedText';
import { classes } from './utils';

export type EventProps = {
  condensed?: boolean;
  style?: CSSProperties;
  className?: string;
} & (
  | {
      orderTask: DecoratedOrderTask;
      unisonProjectTask?: never;
    }
  | {
      orderTask?: never;
      unisonProjectTask: DecoratedUnisonProjectTask;
    }
);

export default function Event({
  condensed,
  style = {},
  className,
  orderTask,
  unisonProjectTask,
}: EventProps) {
  const eventRef = useRef(null);
  const { onSelectProject, onSelectOrder } = useAppState();
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isEditDialogShown, toggleEditDialog] = useToggle();
  const { dueAt, isResolved, assignedTo, description } = (
    unisonProjectTask || orderTask
  ).task;

  const handleEditTask = () => {
    toggleMenu(false);
    toggleEditDialog(true);
  };

  const sharedProps = {
    open: true,
    anchorTo: eventRef.current,
    onEdit: handleEditTask,
    onClose: () => toggleMenu(false),
  };

  const eventClassName = twMerge(
    condensed ? classes.condensedEvent : classes.event,
    isResolved ? classes.closed : classes.open
  );

  return (
    <>
      <div
        ref={eventRef}
        style={style}
        className={className}
        onClick={() => toggleMenu()}>
        <div className={eventClassName}>
          {condensed ? (
            <>
              <Tooltip arrow placement="top" title={formatUserName(assignedTo)}>
                <ProfilePicture size={18} user={assignedTo} />
              </Tooltip>
              <div className={classes.condensedDescription}>
                <TruncatedText>{description}</TruncatedText>
              </div>
            </>
          ) : (
            <>
              <div className={classes.title}>
                <div className={classes.time}>{formatDateOnlyTime(dueAt)}</div>
                <div className={classes.name}>{formatUserName(assignedTo)}</div>
              </div>
              <div className={classes.description}>
                <Markdown>{description}</Markdown>
              </div>
            </>
          )}
        </div>
      </div>
      {isMenuOpen && unisonProjectTask && (
        <EventMenu
          title={getProjectTitleWithPatient(unisonProjectTask.unisonProject)}
          task={unisonProjectTask.task}
          onSelect={() => onSelectProject(unisonProjectTask.unisonProjectId)}
          {...sharedProps}
        />
      )}
      {isMenuOpen && orderTask && (
        <EventMenu
          title={`Order #${orderTask.orderId}`}
          task={orderTask.task}
          onSelect={() => onSelectOrder(orderTask.orderId)}
          {...sharedProps}
        />
      )}
      {isEditDialogShown && unisonProjectTask && (
        <UnisonProjectTaskDialog
          projectId={unisonProjectTask.unisonProjectId}
          taskId={unisonProjectTask.taskId}
          onClose={() => toggleEditDialog()}
        />
      )}
      {isEditDialogShown && orderTask && (
        <OrderTaskDialog
          orderId={orderTask.orderId}
          taskId={orderTask.taskId}
          onClose={() => toggleEditDialog()}
        />
      )}
    </>
  );
}
